import { useEffect, useRef, useState } from 'react';
import { Modal, Typography, Button, Spin, Row, Col, Tooltip } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import './FileDetailsModal.scss';
import PermissionManagerService from '../../../../../services/FileManager/permissionManagement.service';
import { formatBytes, NO_PREVIEW_EXTS } from '../../../constants';
import { resolvedTileIcon } from '../../fileListShared';
import type { FileNode } from '../../../../../types/FileManagerTypes';

const { getUser } = PermissionManagerService();

const { Text } = Typography;

const formatDate = (dateString?: string | Date): string => {
  if (!dateString) return '';
  return dayjs(dateString).format('MMM DD, YYYY').toUpperCase();
};

interface FileLocationMapEntry {
  id: string;
  name: string;
  parentId: string | null;
}

export type FileLocationMap = Record<string, FileLocationMapEntry>;

export const generateFilePath = (fileMap: FileLocationMap, parentFolderId: string): string => {
  const path: string[] = [];

  let currentFolderId: string | null = parentFolderId;

  while (currentFolderId && fileMap[currentFolderId]) {
    const currentFolder: FileLocationMapEntry = fileMap[currentFolderId];

    path.unshift(currentFolder.name);

    currentFolderId = currentFolder.parentId;
  }

  return path.join(' > ');
};

// Shows the tooltip only when the value is actually clipped by ellipsis.
const EllipsisText = ({ value, className, onClick }: { value: string; className?: string; onClick?: () => void }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [overflowed, setOverflowed] = useState(false);

  const checkOverflow = () => {
    const el = ref.current;
    if (el) setOverflowed(el.scrollWidth > el.clientWidth);
  };

  return (
    <div className="file-details-modal__pill">
      <Tooltip arrow={false} title={overflowed ? value : ''} mouseEnterDelay={0.55}>
        <span
          ref={ref}
          className={onClick ? `${className} file-details-modal__value--link` : className}
          onMouseEnter={checkOverflow}
          onClick={onClick}
        >
          {value}
        </span>
      </Tooltip>
    </div>
  );
};

interface FileDetailsModalProps {
  show: boolean;
  hide: () => void;
  selectedFile?: FileNode;
  retrieveFileLocation: (fileId: string) => Promise<{ fileMap: FileLocationMap; parentFolderId: string } | undefined>;
  handleFileOpen: (file: FileNode) => void;
  onNavigateToLocation: (folderId: string) => void;
}

export const FileDetailsModal = ({ show, hide, selectedFile, retrieveFileLocation, handleFileOpen, onNavigateToLocation }: FileDetailsModalProps) => {
  const [fileLocationMap, setFileLocationMap] = useState<FileLocationMap>();
  const [parentFolder, setParentFolder] = useState<string>();
  const [fileLocationLoading, setFileLocationLoading] = useState<boolean>(true);

  const handleOpen = () => selectedFile && handleFileOpen(selectedFile);

  const fileExtension = selectedFile?.name.split('.').pop()?.toLowerCase();
  const isOpenDisabled = !selectedFile?.isDir && !!fileExtension && NO_PREVIEW_EXTS.has(fileExtension);

  const { data: creatorData, isLoading: creatorLoading } = useQuery({
    ...getUser(selectedFile?.creatorId),
    enabled: !!selectedFile?.creatorId,
  });
  const { data: lastModifierData, isLoading: lastModifierLoading } = useQuery({
    ...getUser(selectedFile?.lastAccessUserId),
    enabled: !!selectedFile?.lastAccessUserId,
  });

  useEffect(() => {
    if (!selectedFile?.id) {
      setFileLocationMap(undefined);
      setParentFolder(undefined);
      setFileLocationLoading(false);
      return;
    }

    const fetchLocation = async () => {
      setFileLocationLoading(true);
      const fileLocationData = await retrieveFileLocation(selectedFile.id);

      if (fileLocationData) {
        setFileLocationMap(fileLocationData.fileMap);
        setParentFolder(fileLocationData.parentFolderId);
      }

      setFileLocationLoading(false);
    };

    fetchLocation();
  }, [selectedFile, retrieveFileLocation]);

  const location = fileLocationMap && parentFolder ? generateFilePath(fileLocationMap, parentFolder) : '';

  const loading = creatorLoading || lastModifierLoading || fileLocationLoading;

  const handleLocationClick = () => parentFolder && onNavigateToLocation(parentFolder);

  return (
    <Modal open={show} onCancel={hide} centered maskClosable={true} closeIcon={<></>} footer={null} className="file-details-modal">
      {loading ? (
        <div className="file-details-modal__spinner">
          <Spin />
        </div>
      ) : selectedFile ? (
        <>
          <div className="file-details-modal__top">
            <img
              className="file-details-modal__icon"
              src={selectedFile.isDir ? resolvedTileIcon(selectedFile) : '/assets/generic-file-icon.svg'}
              alt={selectedFile.isDir ? 'folder' : 'file'}
            />
            <div className="file-details-modal__fields">
              <div className="file-details-modal__field">
                <Text className="file-details-modal__label">File Name</Text>
                <EllipsisText className="file-details-modal__value" value={selectedFile.name} />
              </div>
              <div className="file-details-modal__field">
                <Text className="file-details-modal__label">File Permissions</Text>
                <EllipsisText className="file-details-modal__value" value="" />
              </div>
              <div className="file-details-modal__field">
                <Text className="file-details-modal__label">File Location</Text>
                <EllipsisText className="file-details-modal__value" value={location} onClick={parentFolder ? handleLocationClick : undefined} />
              </div>
              <div className="file-details-modal__field">
                <Text className="file-details-modal__label">File Size</Text>
                <EllipsisText className="file-details-modal__value" value={formatBytes(selectedFile.size)} />
              </div>
            </div>
          </div>

          <Row className="file-details-modal__grid" gutter={[16, 12]}>
            <Col span={12} className="file-details-modal__field">
              <Text className="file-details-modal__label">Uploaded By</Text>
              <EllipsisText className="file-details-modal__value" value={creatorData?.name ?? ''} />
            </Col>
            <Col span={12} className="file-details-modal__field">
              <Text className="file-details-modal__label">Uploaded Date</Text>
              <EllipsisText className="file-details-modal__value" value={formatDate(selectedFile.creationDate)} />
            </Col>
            <Col span={12} className="file-details-modal__field">
              <Text className="file-details-modal__label">Last Modified By</Text>
              <EllipsisText className="file-details-modal__value" value={lastModifierData?.name ?? ''} />
            </Col>
            <Col span={12} className="file-details-modal__field">
              <Text className="file-details-modal__label">Last Modified Date</Text>
              <EllipsisText className="file-details-modal__value" value={formatDate(selectedFile.lastAccessTime)} />
            </Col>
          </Row>

          <div className="file-details-modal__actions">
            <Button className="file-details-modal__button" onClick={hide}>
              Back
            </Button>
            <Button className="file-details-modal__button" onClick={handleOpen} disabled={isOpenDisabled}>
              Open
            </Button>
          </div>
        </>
      ) : (
        <Text className="file-details-modal__value">No file selected.</Text>
      )}
    </Modal>
  );
};
